import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {Container, Row, Col, Nav, NavItem, NavLink, Button} from 'react-bootstrap';
import './TabbedContainer.scss'
import { useMatomo } from '@jonkoops/matomo-tracker-react'

/**
 * A tabbed container that will show different children depending on the active
 * tab.
 */
function TabbedContainer({
  defaultIndex = 0,
  showNextAndPreviousTop = false,
  topButtonVariant = "secondary",
  showNextAndPreviousBottom = false,
  bottomButtonVariant = "secondary",
  onBeforeTabChange,
  onAfterTabChange,
  titles = [],
  children = [],
  trackEvents = true,
  eventCategory = 'tabbedContainer',
  ...props
}) {
  const [tabIndex, setTabIndex] = useState(defaultIndex);
  const [highestAllowedIndex, setHighestAllowedIndex] = useState(1);
  const { trackEvent }  = useMatomo();

  function getNavItems() {
    return (
      titles.map(
        (title, i) => {
          return(
            <NavItem>
              <NavLink
                eventKey={i}
                disabled={i > highestAllowedIndex}
                
              >
                {title}
              </NavLink>
            </NavItem>
          )
        }
      )  
    )
  }

  function getActiveChild() {
    if (tabIndex > children.length) {
      return 0
    }
    else {
      return (
        children[tabIndex]  
      )
    }
  }

  /**
   * Changes the index of the component whilst calling the callbacks if set.
   * If the onBeforeTabChange callback returns false, the tab change will be
   * prevented unless blockOnFalseReturn is set to False.
   * @param {int} to Index to change to.
   */
  function changeTabTo(to) {
    to = parseInt(to)
    const from = parseInt(tabIndex);
    
    let currentTabIsValid = true
    let preventTabChange  = false
    // Run our validation callback if set.
    if(typeof(onBeforeTabChange) === 'function') {
      [currentTabIsValid, preventTabChange] = onBeforeTabChange(from, to);
    }

    // Track highest allowed index locally, as setState will update is async, and
    // we need it immediately in this function.
    let localHighestAllowedIndex = highestAllowedIndex;
    // Then update the highest allowed tab.
    // If the current tab is valid, and the highest allowed index is currently
    // this tab (or lower, it never should be), we can raise it by one.
    if(currentTabIsValid && highestAllowedIndex <= from) {
      localHighestAllowedIndex = from + 1;
      setHighestAllowedIndex(localHighestAllowedIndex);
    }
    // If this tab isn't valid, we set this tab to the highest allowed.
    else if(!currentTabIsValid) {
      localHighestAllowedIndex = from;
      setHighestAllowedIndex(localHighestAllowedIndex);
    }
    // Only prevent if the onBefore returned false.
    // Don't call onAfter if we didn't change tab.
    if(!preventTabChange && to <= localHighestAllowedIndex) {
      setTabIndex(to)
      if(trackEvents) {
        trackEvent({
          category: eventCategory,
          action: 'changeFormTabTo',
          name: titles[to],
        });
      }
      if(typeof(onAfterTabChange) === 'function') {
        onAfterTabChange(from, to);
      }
    }
  }

  function getNextAndPrevious(variant) {
    return (
      <Row className="py-2 justify-content-between">
        <Col xs={1} className="px-0">
          {
            tabIndex <= 0 ? null :
            <div className="d-grid gap-2">
              <Button
                variant={variant}
                onClick={() => {
                  changeTabTo(tabIndex - 1);
                }}
              >
                Previous
              </Button>
            </div>
          }
        </Col>
        <Col xs={1} className="px-0">
          {
            tabIndex >= children.length - 1 ? null :
            <div className="d-grid gap-2">
              <Button
                variant={variant}
                onClick={() => {
                  changeTabTo(tabIndex + 1);
                }}
              >
                Next
              </Button>
            </div>
          }
        </Col>
      </Row>  
    )
  }

  return (
    <Container fluid="sm">
      <Row className='px-0'>
        <Col className='px-0'>
          <Nav
            variant="tabs"
            defaultActiveKey="runs"
            activeKey={tabIndex}
            onSelect = {
              (key) => {
                changeTabTo(key)
              }
            }
          >
            {getNavItems()}
          </Nav>
        </Col>
      </Row>
      {
        showNextAndPreviousTop ? getNextAndPrevious(topButtonVariant) : null
      }
      <Row>
        <Col className='px-0'>
          {getActiveChild()}
        </Col>
      </Row>
      {
        showNextAndPreviousBottom ? getNextAndPrevious(bottomButtonVariant) : null
      }
    </Container>
  );
}


TabbedContainer.propTypes = {
  /**
   * Index the tabbed component should default as active. 0 indexed.
   */
  defaultIndex: PropTypes.number,
  /**
   * Array of the titles for the various tabs.
   */
  titles: PropTypes.arrayOf(PropTypes.string),
  /**
   * Array of children to be displayed in the corresponding tabs.
   * Must be same length as titles.
   */
  children: PropTypes.arrayOf(PropTypes.element),
  /**
   * Whether to show previous and next buttons at the top of the tab contents.
   */
  showNextAndPreviousTop: PropTypes.bool,
  /**
   * Bootstrap variant to use for the top buttons.
   */
  topButtonVariant: PropTypes.oneOf([
    "primary",
    "secondary",
    "success",
    "warning",
    "danger",
    "info",
    "light",
    "dark",
    "link",
  ]),
  /**
   * Whether to show previous and next buttons at the bottom of the tab contents.
   */
  showNextAndPreviousBottom: PropTypes.bool,
  /**
   * Bootstrap variant to use for the bottom buttons.
   */
  bottomButtonVariant: PropTypes.oneOf([
    "primary",
    "secondary",
    "success",
    "warning",
    "danger",
    "info",
    "light",
    "dark",
    "link",
  ]),
  /**
   * Callback function that will be called just before tab is changed.
   * Will be passed two arguments, index navigated from, and index navigated to.
   * Returns an array of two boolean values
   * [currentTabIsValid, preventTabChange]
   * currentTabIsValid will be used to track the highest valid tab, which only
   * allows the user to click forward through tabs they ahve already completed.
   * preventTabChange will block the tab change is set to false.
   */
  onBeforeTabChange: PropTypes.func,
  /**
   * Callback function that will be called just after tab is changed.
   * Function return will be ignored.
   * Will pass two arguments, index navigated from, and index navigated to.
   */
  onAfterTabChange: PropTypes.func,
  /**
   * Whether or not tab changes should send actions to matomo.
   */
  trackEvents: PropTypes.bool,
  /**
   * eventCategory to use when tracking tab changes in matomo.
   */
  eventCategory: PropTypes.string,
};

export default TabbedContainer;